import { ObjectKeys } from "fond/types";

/**
 * a numeric enum for log levels
 * This makes it easy to detemine whether the current log level should
 */
export const AnalyticsLogLevel = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
} as const;
export type AnalyticsLogLevel = ObjectKeys<typeof AnalyticsLogLevel>;

type AnalyticsLoggerTransportMethod = (message: string, ...optionalParams: unknown[]) => void;

export type AnalyticsLoggerTransport = Record<AnalyticsLogLevel, AnalyticsLoggerTransportMethod>;

class AnalyticsLogger {
  constructor(
    private logLevel: AnalyticsLogLevel | undefined = undefined,
    private transport: AnalyticsLoggerTransport = console
  ) {}

  private shouldLog(requestedLogLevel: AnalyticsLogLevel) {
    if (!this.logLevel || !Object.keys(AnalyticsLogLevel).includes(this.logLevel)) {
      return false;
    }
    // Get the log level from the environment variable, default it to not logging if not set or set to an invalid value;
    const logLevelNumber = AnalyticsLogLevel[this.logLevel];
    const requestedLevelNumber = AnalyticsLogLevel[requestedLogLevel];

    return requestedLevelNumber >= logLevelNumber;
  }

  private log(level: AnalyticsLogLevel, event: string, ...rest: unknown[]): void {
    if (!this.shouldLog(level)) {
      return;
    }
    const s1 = "color: light-dark(#14a, #69f); font-weight: bold;";
    const s2 = "color: light-dark(#666, #aaa); font-weight: normal;";

    this.transport[level](`%c[Analytics]%c[${event}]`, s1, s2, ...rest);
  }

  public debug(event: string, ...rest: unknown[]): void {
    this.log("debug", event, ...rest);
  }

  public info(event: string, ...rest: unknown[]): void {
    this.log("info", event, ...rest);
  }

  public warn(event: string, ...rest: unknown[]): void {
    this.log("warn", event, ...rest);
  }

  public error(event: string, ...rest: unknown[]): void {
    this.log("error", event, ...rest);
  }
}

export default AnalyticsLogger;
